import { Link } from 'react-router';
import { useLesMarques } from '../features/marques/marques';
import { Bouton } from '../shared/ui/Bouton';
import { Carrousel } from '../shared/ui/Carrousel';
import { Video } from '../shared/ui/Media';
import { Paragraphe, Section } from '../shared/ui/Section';
import { Titre } from '../shared/ui/Titre';
import { EtatErreur, EtatVide, Squelette } from '../shared/ui/TroisEtats';
import { lireLErreur } from '../shared/api/client';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **L'ACCUEIL — PAGE 1 DES HUIT, ET LA SEULE QUI LIT LA BASE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-69 le dit dans sa table : « **Accueil**, la présentation du produit — **seule page qui
 * lit la base**, par le carrousel des marques ». Les sept autres sont du code servi.
 *
 * **Elle traite donc LES TROIS ÉTATS**, et elle est la seule page du site à en avoir :
 * `site/CLAUDE.md` — « chargement par squelette, erreur avec réessai, et vide avec un
 * message utile. **Une page qui n'en traite qu'un est inachevée.** »
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **UNE PERSONNE DÉJÀ CONNECTÉE VOIT EXACTEMENT CE QUE VOIT UN INCONNU.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `CDC-6` §13, cas de bord : « **une personne déjà connectée ouvre l'accueil → elle voit
 * exactement ce que voit un inconnu.** » Cette page ne lit donc **aucune session**, et ce
 * n'est pas une économie : F-71 pose que « les sept autres pages **ne savent rien de
 * personne** ». Le seul endroit du site qui regarde une session est la page 6.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QU'ELLE NE PORTE PAS, ET CHAQUE ABSENCE EST UNE RÈGLE ÉCRITE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * - **Aucun tarif** : « **AUCUN TARIF NULLE PART SUR LE SITE.** Le prix se discute hors du
 *   site » (F-69). Il n'y a donc ni grille, ni « à partir de », ni essai gratuit.
 * - **Aucune inscription, aucun bouton de création de compte** (§12) : « le produit ne
 *   s'ouvre à personne tout seul », une structure provisionne.
 * - **Aucune capture d'écran réelle du produit** (F-74), donc aucune écriture manuscrite,
 *   aucun prénom, aucun titre de cours. Ce qui est montré est une **vidéo publicitaire**.
 * - **Aucun chiffre du produit** : « aucun chiffre calculé sur les données du produit, sur
 *   aucune page » (F-74). Les seuls chiffres de cette page sont les **valeurs saisies** des
 *   marques, et le carrousel les rend telles quelles.
 * - **Aucune mesure d'audience** (F-73). Rien n'est compté, rien n'est déposé.
 * - **Rien qui s'adresse à une famille** : « Swype se vend aux structures de soutien
 *   scolaire et aux tuteurs indépendants, **jamais aux familles** » (§1), et le brief le
 *   redit — « le site s'adresse aux structures et aux tuteurs, **jamais à un parent** ».
 */
export function PageAccueil() {
  const marques = useLesMarques();

  return (
    <>
      <Section largeur="etroite">
        <Titre niveau={1}>
          Le tableau blanc au stylet de vos cours à distance
        </Titre>
        <Paragraphe>
          Swype réunit un tuteur et ses élèves autour d’un tableau qu’ils
          écrivent à la main, tous les deux, en même temps. La visio est là
          quand elle sert, et le temps de cours se mesure tout seul.
        </Paragraphe>
        <Paragraphe>
          Il s’adresse aux structures de soutien scolaire et aux tuteurs
          indépendants. Les familles n’ont ni compte à ouvrir, ni rien à payer.
        </Paragraphe>
        <div className="flex flex-wrap gap-3">
          <Link to="/structures">
            <Bouton>Pour une structure</Bouton>
          </Link>
          <Link to="/tuteurs">
            <Bouton variante="douce">Pour un tuteur</Bouton>
          </Link>
        </div>
      </Section>

      {/* **L'une des deux vidéos**, F-74. Laquelle va où est une décision de composition et
          non de spécification : le brief de maquette range « la répartition des deux vidéos
          entre l'accueil et la page du détail » parmi ce qui n'est pas une décision de
          spécification. Elle est servie **depuis le domaine du produit** — voir `Media`. */}
      <Section largeur="large">
        <Video
          fichier="presentation.mp4"
          description="Un tuteur et son élève écrivent sur le même tableau, chacun de son côté."
        />
      </Section>

      <Section titre="Ce que Swype fait, et ce qu’il ne fait pas">
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Titre niveau={3} taille="18">
              Un tableau au stylet, à deux mains
            </Titre>
            <Paragraphe>
              Le tuteur et l’élève écrivent sur la même page, au stylet, et
              voient le trait de l’autre apparaître. Le cours entier tient dans
              un seul écran : on n’y navigue pas, on y travaille.
            </Paragraphe>
          </div>
          <div className="flex flex-col gap-2">
            <Titre niveau={3} taille="18">
              La visio d’appoint
            </Titre>
            <Paragraphe>
              La voix et l’image servent à s’expliquer, pas à se regarder. Elles
              s’allument et s’éteignent sans quitter le tableau.
            </Paragraphe>
          </div>
          <div className="flex flex-col gap-2">
            <Titre niveau={3} taille="18">
              Le temps de cours, mesuré
            </Titre>
            <Paragraphe>
              Chaque entrée et chaque sortie sont relevées. La structure lit des
              durées, pas des déclarations.
            </Paragraphe>
          </div>
          <div className="flex flex-col gap-2">
            <Titre niveau={3} taille="18">
              Aucune facturation
            </Titre>
            <Paragraphe>
              Swype ne facture personne et ne parle pas d’argent aux familles.
              La relation commerciale reste celle de la structure avec les
              siennes.
            </Paragraphe>
          </div>
        </div>
      </Section>

      {/* ═══════════════════════════════════════════════════════════════════
          **LE CARROUSEL DES MARQUES — ICI, ET SUR AUCUNE AUTRE PAGE.**
          ═══════════════════════════════════════════════════════════════════

          F-70 : « le carrousel vit sur l'accueil, et sur cette page seule. **Aucun bandeau,
          aucun pied de page ne le répète.** »

          **Les trois états sont traités, et le vide n'est pas une panne** : F-70 — « le
          carrousel a un état vide, quand aucune structure n'a ses trois colonnes
          déclencheuses remplies ». C'est un état du parc, pas un défaut du site. */}
      <Section titre="Elles utilisent Swype">
        {marques.isPending && <Squelette lignes={3} />}

        {marques.isError && (
          <EtatErreur
            message={lireLErreur(marques.error).message}
            onReessayer={() => void marques.refetch()}
          />
        )}

        {marques.isSuccess &&
          (marques.data.marques.length === 0 ? (
            <EtatVide titre="Aucune structure n’est nommée ici pour l’instant">
              Les structures qui utilisent Swype apparaissent sur cette page
              lorsqu’elles ont donné leur accord pour être nommées.
            </EtatVide>
          ) : (
            <Carrousel marques={marques.data.marques} />
          ))}
      </Section>

      <Section largeur="etroite" titre="Parler du produit">
        <Paragraphe>
          Le prix, le périmètre et la mise en place se discutent de vive voix.
          Écrivez-nous, on vous répond.
        </Paragraphe>
        <div className="flex flex-wrap gap-3">
          <Link to="/contact">
            <Bouton>Nous écrire</Bouton>
          </Link>
          <Link to="/produit">
            <Bouton variante="faible">Voir le produit en détail</Bouton>
          </Link>
        </div>
      </Section>
    </>
  );
}
